// #region calendar.helpers
// Time-zone aware date maths for the calendar and booking pages, on Intl only.
// Everything the server does in zoned_time.ts, mirrored for the browser.

export interface ZonedParts { year: number; month: number; day: number; hour: number; minute: number; weekday: number }

const WEEKDAYS: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
const cache = new Map<string, Intl.DateTimeFormat>();

const fmt = (timeZone: string): Intl.DateTimeFormat => {
    let f = cache.get(timeZone);
    if (!f) {
        f = new Intl.DateTimeFormat('en-US', { timeZone, hourCycle: 'h23', weekday: 'short', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
        cache.set(timeZone, f);
    }
    return f;
};

export const browserTimeZone = (): string => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
};

export const isValidTimeZone = (tz: string): boolean => { try { fmt(tz); return true; } catch { return false; } };

export const zonedParts = (instant: Date, timeZone: string): ZonedParts => {
    const p: Record<string, string> = {};
    for (const part of fmt(timeZone).formatToParts(instant)) p[part.type] = part.value;
    return { year: +p.year, month: +p.month, day: +p.day, hour: +p.hour % 24, minute: +p.minute, weekday: WEEKDAYS[p.weekday] ?? 0 };
};

const offsetMinutes = (instant: Date, timeZone: string): number => {
    const p = zonedParts(instant, timeZone);
    return Math.round((Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute) - instant.getTime()) / 60_000);
};

/** The instant at a wall-clock time in a zone (two-pass for DST edges). */
export const zonedTimeToUtc = (year: number, month: number, day: number, hour: number, minute: number, timeZone: string): Date => {
    const guess = Date.UTC(year, month - 1, day, hour, minute);
    const first = new Date(guess - offsetMinutes(new Date(guess), timeZone) * 60_000);
    return new Date(guess - offsetMinutes(first, timeZone) * 60_000);
};

export const dateKey = (instant: Date, timeZone: string): string => {
    const p = zonedParts(instant, timeZone);
    return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
};

export const keyParts = (key: string): { y: number; m: number; d: number } => ({ y: +key.slice(0, 4), m: +key.slice(5, 7), d: +key.slice(8, 10) });

/** Start of the local day holding `instant`, plus the start of the next day. */
export const dayBounds = (instant: Date, timeZone: string): { start: Date; end: Date; key: string } => {
    const p = zonedParts(instant, timeZone);
    const start = zonedTimeToUtc(p.year, p.month, p.day, 0, 0, timeZone);
    const n = new Date(Date.UTC(p.year, p.month - 1, p.day + 1));
    const end = zonedTimeToUtc(n.getUTCFullYear(), n.getUTCMonth() + 1, n.getUTCDate(), 0, 0, timeZone);
    return { start, end, key: dateKey(start, timeZone) };
};

/** The seven local days of the week containing `instant`, starting Monday. */
export const weekDays = (instant: Date, timeZone: string): { start: Date; end: Date; key: string }[] => {
    const first = dayBounds(instant, timeZone);
    const wd = zonedParts(first.start, timeZone).weekday;
    const back = (wd + 6) % 7;   // days since Monday
    let cursor = first.start;
    for (let i = 0; i < back; i += 1) cursor = dayBounds(new Date(cursor.getTime() - 3600_000), timeZone).start;
    const days: { start: Date; end: Date; key: string }[] = [];
    for (let i = 0; i < 7; i += 1) { const d = dayBounds(cursor, timeZone); days.push(d); cursor = d.end; }
    return days;
};

/** Minutes since local midnight of an instant, for placing blocks on a time grid. */
export const minutesIntoDay = (instant: Date, timeZone: string): number => {
    const p = zonedParts(instant, timeZone);
    return p.hour * 60 + p.minute;
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const DAYS_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const hm12 = (hour: number, minute: number): string => {
    const h = hour % 12 === 0 ? 12 : hour % 12;
    return `${h}${minute ? `:${String(minute).padStart(2, '0')}` : ''} ${hour < 12 ? 'AM' : 'PM'}`;
};

/** "4:00 PM" in a zone. */
export const timeIn = (iso: string, timeZone: string): string => { const p = zonedParts(new Date(iso), timeZone); return hm12(p.hour, p.minute); };

/** "Thu, Oct 29" in a zone. */
export const dayIn = (iso: string | Date, timeZone: string): string => {
    const p = zonedParts(typeof iso === 'string' ? new Date(iso) : iso, timeZone);
    return `${DAYS[p.weekday]}, ${MONTHS[p.month - 1]} ${p.day}`;
};

/** "Thursday, October 29" */
export const dayInLong = (iso: string | Date, timeZone: string): string => {
    const p = zonedParts(typeof iso === 'string' ? new Date(iso) : iso, timeZone);
    return `${DAYS_LONG[p.weekday]}, ${MONTHS_LONG[p.month - 1]} ${p.day}`;
};

/** "4:00 – 4:30 PM" */
export const rangeIn = (startIso: string, endIso: string, timeZone: string): string => {
    const s = zonedParts(new Date(startIso), timeZone);
    const e = zonedParts(new Date(endIso), timeZone);
    const sameHalf = (s.hour < 12) === (e.hour < 12);
    const left = sameHalf ? hm12(s.hour, s.minute).replace(/ [AP]M$/, '') : hm12(s.hour, s.minute);
    return `${left} – ${hm12(e.hour, e.minute)}`;
};

export const zoneAbbrev = (instant: Date, timeZone: string): string => {
    try {
        return new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' }).formatToParts(instant).find((p) => p.type === 'timeZoneName')?.value ?? timeZone;
    } catch { return timeZone; }
};

/** "America/Phoenix" → "Phoenix (MST)". */
export const zoneLabel = (timeZone: string, at = new Date()): string =>
    `${timeZone.split('/').pop()?.replace(/_/g, ' ') ?? timeZone} (${zoneAbbrev(at, timeZone)})`;

/** Every zone the browser knows, else a sensible short list. */
export const timeZoneOptions = (): string[] => {
    const all = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('timeZone');
    if (all && all.length) return all;
    return ['America/Phoenix', 'America/Los_Angeles', 'America/Denver', 'America/Chicago', 'America/New_York', 'America/Toronto', 'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Asia/Dubai', 'Asia/Singapore', 'Asia/Tokyo', 'Australia/Sydney', 'UTC'];
};

export const durationLabel = (min: number): string =>
    min < 60 ? `${min} min` : min % 60 === 0 ? `${min / 60} hr${min > 60 ? 's' : ''}` : `${Math.floor(min / 60)} hr ${min % 60} min`;
